import { useState } from "react";
import { Activity, Gauge, Play, Power } from "lucide-react";
import type { SimulatorAppSurfaceProps } from "../AppSurface.js";

const processes = [
  { name: "FD Explorer", processName: "explorer.exe", status: "Running", cpu: "7%", memory: "620 MB", disk: "0 MB/s", pid: 1840 },
  { name: "FD Terminal", processName: "terminal.exe", status: "Running", cpu: "2%", memory: "180 MB", disk: "0 MB/s", pid: 2288 },
  { name: "Notepad", processName: "notepad.exe", status: "Running", cpu: "1%", memory: "82 MB", disk: "0 MB/s", pid: 4040 },
  { name: "Service Host: Network", processName: "svchost.exe", status: "Running", cpu: "3%", memory: "96 MB", disk: "0 MB/s", pid: 984 }
];

export function TaskManagerApp({ activeMachine, onOpenApp, onRunCommand }: SimulatorAppSurfaceProps) {
  const [processName, setProcessName] = useState("notepad.exe");
  const [pid, setPid] = useState("4040");
  const [itemName, setItemName] = useState("FD Sync");
  const [startupEnabled, setStartupEnabled] = useState(false);
  const [serviceName, setServiceName] = useState("Spooler");

  async function endProcess(nextProcessName = processName, nextPid = pid) {
    await onRunCommand({
      appId: "taskmgr",
      commandType: "PROCESS_END",
      capability: "local.process.write",
      label: `End ${nextProcessName}`,
      payload: { machineId: activeMachine, pid: Number(nextPid), processName: nextProcessName }
    });
  }

  async function toggleStartup(nextEnabled = startupEnabled) {
    await onRunCommand({
      appId: "taskmgr",
      commandType: "STARTUP_ITEM_TOGGLE",
      capability: "local.startup.write",
      label: `${nextEnabled ? "Enable" : "Disable"} ${itemName}`,
      payload: { enabled: nextEnabled, itemName, machineId: activeMachine }
    });
    setStartupEnabled(nextEnabled);
  }

  async function startService() {
    await onRunCommand({
      appId: "taskmgr",
      commandType: "SERVICE_START_FROM_TASKMGR",
      capability: "service.write",
      label: `Start ${serviceName}`,
      payload: { machineId: activeMachine, serviceName }
    });
  }

  return (
    <div className="fd-sim-app fd-task-manager">
      <aside className="fd-task-manager-nav">
        <button className="is-active" type="button"><Activity size={16} /> Processes</button>
        <button type="button"><Gauge size={16} /> Performance</button>
        <button type="button" onClick={() => onOpenApp("lusrmgr", "start")}>Users</button>
        <button type="button" onClick={() => void toggleStartup(!startupEnabled)}>Startup apps</button>
        <button type="button" onClick={() => onOpenApp("services", "start")}>Services</button>
      </aside>
      <main className="fd-task-manager-main">
        <header>
          <div>
            <p>{activeMachine}</p>
            <h2>Processes</h2>
          </div>
          <button type="button" onClick={() => void endProcess()}>End task</button>
        </header>
        <div className="fd-task-manager-summary">
          <span>CPU 18%</span>
          <span>Memory 2.1 GB</span>
          <span>Disk 4%</span>
          <span>Network 1 Mbps</span>
        </div>
        <table className="fd-task-manager-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>CPU</th>
              <th>Memory</th>
              <th>Disk</th>
              <th>PID</th>
            </tr>
          </thead>
          <tbody>
            {processes.map((process) => (
              <tr
                key={process.pid}
                onDoubleClick={() => {
                  setProcessName(process.processName);
                  setPid(String(process.pid));
                  void endProcess(process.processName, String(process.pid));
                }}
              >
                <td><button className="fd-mmc-link" type="button" onClick={() => { setProcessName(process.processName); setPid(String(process.pid)); }}>{process.name}</button></td>
                <td>{process.status}</td>
                <td>{process.cpu}</td>
                <td>{process.memory}</td>
                <td>{process.disk}</td>
                <td>{process.pid}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <section className="fd-mmc-detail-panel">
          <header>
            <div>
              <p>Command controls</p>
              <h3>{processName}</h3>
            </div>
            <div className="fd-mmc-inline-actions">
              <button type="button" onClick={() => void toggleStartup(!startupEnabled)}><Power size={14} /> Startup</button>
              <button type="button" onClick={() => void startService()}><Play size={14} /> Service</button>
            </div>
          </header>
          <div className="fd-command-form">
            <label>
              Process
              <input value={processName} onChange={(event) => setProcessName(event.target.value)} />
            </label>
            <label>
              PID
              <input value={pid} onChange={(event) => setPid(event.target.value)} />
            </label>
            <label>
              Startup item
              <input value={itemName} onChange={(event) => setItemName(event.target.value)} />
            </label>
            <label>
              Service
              <input value={serviceName} onChange={(event) => setServiceName(event.target.value)} />
            </label>
          </div>
        </section>
      </main>
    </div>
  );
}
